import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageBubble } from "@/components/message-bubble";
import { useMode } from "@/lib/mode-provider";
import { api } from "@/lib/api";

interface ConversationMessage {
  role: "agent" | "customer";
  name: string;
  text: string;
  timestamp: string;
  image_url?: string;
}

interface Conversation {
  id: string;
  date: string;
  messages: ConversationMessage[];
}

interface DisplayMessage {
  role: "customer" | "trainee" | "real-agent";
  text: string;
  label?: string;
  imageUrl?: string;
  timestamp?: string;
}

interface BestResponse {
  messageIndex: number;
  traineeName: string;
  text: string;
  bestComment?: string | null;
}

interface TrainingChatProps {
  categoryId: string;
  categoryName: string;
  conversationId?: string;
  mode?: "sequential" | "random" | "browse";
  onChangeCategory: () => void;
  onBackToModeSelect?: () => void;
  /**
   * When set, fires INSTEAD of the default "load random next" behavior
   * for both the in-flight "Next Conversation" button (mid-session) and
   * the post-finish one. Used by queue pages (e.g. /train/retrain-skipped)
   * to advance to the next item in their own list.
   */
  onNextConversation?: () => void;
}

export function TrainingChat({
  categoryId,
  categoryName,
  conversationId,
  mode: trainingMode = "random",
  onChangeCategory,
  onBackToModeSelect,
  onNextConversation,
}: TrainingChatProps) {
  const { mode, toggleMode } = useMode();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [displayMessages, setDisplayMessages] = useState<DisplayMessage[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [input, setInput] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [completed, setCompleted] = useState(0);
  const [copied, setCopied] = useState(false);
  const [initialIdUsed, setInitialIdUsed] = useState(false);
  const [bestResponses, setBestResponses] = useState<BestResponse[]>([]);
  const [bestTurnCounter, setBestTurnCounter] = useState(0);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>(conversationId);
  const [sessionStartedAt, setSessionStartedAt] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const buildAllMessages = useCallback(
    (conv: Conversation, bestResps?: BestResponse[]): DisplayMessage[] => {
      const msgs = conv.messages;
      const result: DisplayMessage[] = [];
      let turnIdx = 0;
      let prevWasCustomer = false;
      let i = 0;

      while (i < msgs.length) {
        if (msgs[i].role === "agent") {
          if (prevWasCustomer) {
            const bestForTurn = (bestResps || []).find((b) => b.messageIndex === turnIdx);
            if (bestForTurn) {
              const allText = bestForTurn.bestComment
                ? `${bestForTurn.text}\n\n💬 Why: ${bestForTurn.bestComment}`
                : bestForTurn.text;
              result.push({
                role: "real-agent",
                text: allText,
                label: `⭐ Best Response by ${bestForTurn.traineeName}`,
              });
              while (i < msgs.length && msgs[i].role === "agent") i++;
              turnIdx++;
              prevWasCustomer = false;
              continue;
            }
            turnIdx++;
          }
          result.push({
            role: "real-agent",
            text: msgs[i].text,
            label: "Agent",
            imageUrl: msgs[i].image_url,
            timestamp: msgs[i].timestamp,
          });
          prevWasCustomer = false;
          i++;
        } else {
          result.push({
            role: "customer",
            text: msgs[i].text,
            imageUrl: msgs[i].image_url,
            timestamp: msgs[i].timestamp,
          });
          prevWasCustomer = true;
          i++;
        }
      }
      return result;
    },
    [],
  );

  const recordProgress = async (conversationId: string) => {
    try {
      await api("/api/progress", {
        method: "POST",
        body: JSON.stringify({ conversationId, category: categoryId }),
      });
      setCompleted((prev) => prev + 1);
    } catch {
      // silently fail
    }
  };

  const saveSession = (conversationId: string, messages: DisplayMessage[]) => {
    const finishedAt = new Date().toISOString();
    // fire-and-forget
    api("/api/sessions", {
      method: "POST",
      body: JSON.stringify({
        conversationId,
        category: categoryId,
        messages,
        startedAt: sessionStartedAt,
        finishedAt,
      }),
    }).catch(() => {});
  };

  const loadConversation = useCallback(
    async (afterId?: string) => {
      setLoading(true);
      setDisplayMessages([]);
      setCurrentIndex(0);
      setInput("");
      setSubmitted(false);
      setBestTurnCounter(0);
      setFinished(false);

      try {
        const params = new URLSearchParams({ category: categoryId });

        if (conversationId && !initialIdUsed) {
          params.set("id", conversationId);
          setInitialIdUsed(true);
        } else if (afterId && trainingMode === "sequential") {
          params.set("mode", "next");
          params.set("after", afterId);
        } else if (trainingMode === "sequential") {
          params.set("mode", "sequential");
        } else {
          params.set("mode", "random");
        }

        const data = await api<{
          conversation?: Conversation;
          total?: number;
          completed?: number;
        }>(`/api/conversation?${params}`);

        if (data.total !== undefined) setTotal(data.total);
        if (data.completed !== undefined) setCompleted(data.completed);

        if (data.conversation) {
          setConversation(data.conversation);
          setSessionStartedAt(new Date().toISOString());
          setCurrentConversationId(data.conversation.id);

          let fetchedBest: BestResponse[] = [];
          try {
            const bestData = await api<{ bestResponses: BestResponse[] }>(
              `/api/best-response?conversationId=${data.conversation.id}`,
            );
            fetchedBest = bestData.bestResponses || [];
            setBestResponses(fetchedBest);
            setBestTurnCounter(0);
          } catch {
            setBestResponses([]);
          }

          if (mode === "showall") {
            setDisplayMessages(buildAllMessages(data.conversation, fetchedBest));
            setFinished(true);
            setLoading(false);
            return;
          }

          const msgs = data.conversation.messages;
          const display: DisplayMessage[] = [];
          let idx = 0;

          while (idx < msgs.length && msgs[idx].role === "agent") {
            display.push({
              role: "real-agent",
              text: msgs[idx].text,
              label: "Agent greeting",
              imageUrl: msgs[idx].image_url,
              timestamp: msgs[idx].timestamp,
            });
            idx++;
          }
          while (idx < msgs.length && msgs[idx].role === "customer") {
            display.push({
              role: "customer",
              text: msgs[idx].text,
              imageUrl: msgs[idx].image_url,
              timestamp: msgs[idx].timestamp,
            });
            idx++;
          }
          setDisplayMessages(display);
          setCurrentIndex(idx);
        }
      } catch (error) {
        console.error("Failed to load conversation:", error);
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [categoryId, conversationId, initialIdUsed, trainingMode],
  );

  useEffect(() => {
    if (conversation) {
      if (mode === "showall") {
        setDisplayMessages(buildAllMessages(conversation, bestResponses));
        setFinished(true);
      } else {
        const msgs = conversation.messages;
        const display: DisplayMessage[] = [];
        let idx = 0;
        while (idx < msgs.length && msgs[idx].role === "agent") {
          display.push({
            role: "real-agent",
            text: msgs[idx].text,
            label: "Agent greeting",
            imageUrl: msgs[idx].image_url,
            timestamp: msgs[idx].timestamp,
          });
          idx++;
        }
        while (idx < msgs.length && msgs[idx].role === "customer") {
          display.push({
            role: "customer",
            text: msgs[idx].text,
            imageUrl: msgs[idx].image_url,
            timestamp: msgs[idx].timestamp,
          });
          idx++;
        }
        setDisplayMessages(display);
        setCurrentIndex(idx);
        setSubmitted(false);
        setFinished(false);
        setInput("");
        setBestTurnCounter(0);
      }
    } else {
      loadConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, mode]);

  useEffect(() => {
    if (!scrollRef.current) return;
    if (mode === "showall") {
      scrollRef.current.scrollTop = 0;
    } else {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [displayMessages, mode]);

  const handleSubmit = () => {
    if (!input.trim() || !conversation) return;

    const msgs = conversation.messages;
    const newDisplay: DisplayMessage[] = [
      ...displayMessages,
      { role: "trainee", text: input.trim(), label: "Your reply" },
    ];

    let idx = currentIndex;
    const bestForTurn = bestResponses.find((b) => b.messageIndex === bestTurnCounter);
    if (bestForTurn) {
      const bestText = bestForTurn.bestComment
        ? `${bestForTurn.text}\n\n💬 Why: ${bestForTurn.bestComment}`
        : bestForTurn.text;
      newDisplay.push({
        role: "real-agent",
        text: bestText,
        label: `⭐ Best Response by ${bestForTurn.traineeName}`,
      });
      setBestTurnCounter((prev) => prev + 1);
      while (idx < msgs.length && msgs[idx].role === "agent") idx++;
    } else {
      setBestTurnCounter((prev) => prev + 1);
      let shownFirst = false;
      while (idx < msgs.length && msgs[idx].role === "agent") {
        newDisplay.push({
          role: "real-agent",
          text: msgs[idx].text,
          label: shownFirst ? "Agent follow-up" : "Real agent reply",
          imageUrl: msgs[idx].image_url,
          timestamp: msgs[idx].timestamp,
        });
        shownFirst = true;
        idx++;
      }
    }

    setCurrentIndex(idx);
    setDisplayMessages(newDisplay);
    setInput("");
    setSubmitted(true);
  };

  const handleSkip = () => {
    if (!conversation) return;
    const msgs = conversation.messages;

    const newDisplay = [...displayMessages];
    let idx = currentIndex;

    const bestForTurn = bestResponses.find((b) => b.messageIndex === bestTurnCounter);
    if (bestForTurn) {
      const skipText = bestForTurn.bestComment
        ? `${bestForTurn.text}\n\n💬 Why: ${bestForTurn.bestComment}`
        : bestForTurn.text;
      newDisplay.push({
        role: "real-agent",
        text: skipText,
        label: `⭐ Best Response by ${bestForTurn.traineeName} (skipped)`,
      });
      setBestTurnCounter((prev) => prev + 1);
      while (idx < msgs.length && msgs[idx].role === "agent") idx++;
    } else {
      setBestTurnCounter((prev) => prev + 1);
      let shownFirst = false;
      while (idx < msgs.length && msgs[idx].role === "agent") {
        newDisplay.push({
          role: "real-agent",
          text: msgs[idx].text,
          label: shownFirst ? "Agent follow-up" : "Real agent reply (skipped)",
          imageUrl: msgs[idx].image_url,
          timestamp: msgs[idx].timestamp,
        });
        shownFirst = true;
        idx++;
      }
    }
    setCurrentIndex(idx);
    setDisplayMessages(newDisplay);
    setSubmitted(true);
  };

  const handleNext = () => {
    if (!conversation) return;
    const msgs = conversation.messages;

    let idx = currentIndex;
    const newDisplay = [...displayMessages];

    if (idx >= msgs.length) {
      setFinished(true);
      recordProgress(conversation.id);
      saveSession(conversation.id, newDisplay);
      return;
    }

    while (idx < msgs.length && msgs[idx].role === "customer") {
      newDisplay.push({
        role: "customer",
        text: msgs[idx].text,
        imageUrl: msgs[idx].image_url,
        timestamp: msgs[idx].timestamp,
      });
      idx++;
    }

    if (idx >= msgs.length && newDisplay.length === displayMessages.length) {
      setFinished(true);
      recordProgress(conversation.id);
      saveSession(conversation.id, newDisplay);
      return;
    }

    setDisplayMessages(newDisplay);
    setCurrentIndex(idx);
    setSubmitted(false);
  };

  const handleNextConversation = useCallback(() => {
    if (conversation && mode === "showall") {
      recordProgress(conversation.id);
      saveSession(conversation.id, displayMessages);
    }
    if (onNextConversation) {
      onNextConversation();
      return;
    }
    if (trainingMode === "sequential" && currentConversationId) {
      loadConversation(currentConversationId);
    } else {
      loadConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversation,
    mode,
    trainingMode,
    currentConversationId,
    displayMessages,
    loadConversation,
    onNextConversation,
  ]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Enter") return;
      if (mode === "showall") {
        handleNextConversation();
      } else if (finished) {
        // In a queue (onNextConversation provided), Enter advances the queue
        // rather than loading a random next conversation.
        if (onNextConversation) onNextConversation();
        else loadConversation();
      } else if (submitted) {
        handleNext();
      }
    }
    if (mode === "showall" || finished || submitted) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, finished, submitted]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        Loading conversation...
      </div>
    );
  }

  const modeBadge =
    trainingMode === "sequential"
      ? "Sequential"
      : trainingMode === "browse"
        ? "Browse"
        : null;

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-4 border-b bg-gray-50">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBackToModeSelect || onChangeCategory}
          className="shrink-0"
        >
          ← {onBackToModeSelect ? "Back" : "Change Category"}
        </Button>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {modeBadge && (
            <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded">
              {modeBadge}
            </span>
          )}
          {total > 0 && (
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
              {completed} / {total} cases
            </span>
          )}
          {conversation && (
            <button
              className="text-xs text-gray-400 hover:text-blue-500 hover:underline hidden sm:inline"
              title="Copy link to this conversation"
              onClick={() => {
                const url = `${window.location.origin}/train?category=${categoryId}&id=${conversation.id}`;
                navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? "Copied!" : `ID: ${conversation.id}`}
            </button>
          )}
          <span className="text-sm text-gray-500">{categoryName}</span>
          <Button variant="outline" size="sm" onClick={toggleMode}>
            {mode === "training" ? "Show All" : "Training"}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4" ref={scrollRef}>
        <div className="space-y-4">
          {displayMessages.map((msg, i) => (
            <MessageBubble
              key={i}
              role={msg.role}
              text={msg.text}
              label={msg.label}
              imageUrl={msg.imageUrl}
              timestamp={msg.timestamp}
            />
          ))}
        </div>
      </div>

      <div className="p-4 border-t">
        {mode === "showall" ? (
          <div className="text-center space-y-3">
            <div className="flex gap-2 justify-center">
              <Button onClick={handleNextConversation}>Next Conversation</Button>
              <Button variant="outline" onClick={onChangeCategory}>
                Change Category
              </Button>
            </div>
          </div>
        ) : finished ? (
          <div className="text-center space-y-3">
            <p className="text-sm text-gray-500">Conversation complete!</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={handleNextConversation}>
                {trainingMode === "sequential" ? "Next in Sequence →" : "Next Conversation"}
              </Button>
              <Button variant="outline" onClick={onBackToModeSelect || onChangeCategory}>
                {onBackToModeSelect ? "Back to Menu" : "Change Category"}
              </Button>
            </div>
          </div>
        ) : submitted ? (
          <div className="text-center space-y-2">
            <div className="flex gap-2 justify-center">
              <Button onClick={handleNext}>Continue →</Button>
              <Button
                variant="outline"
                onClick={() => (onNextConversation ? onNextConversation() : loadConversation())}
              >
                {onNextConversation ? "Skip to next →" : "Next Conversation"}
              </Button>
            </div>
            <p className="text-xs text-gray-400">Press Enter to continue</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSubmit()}
                placeholder="Type your reply..."
              />
              <Button onClick={handleSubmit} disabled={!input.trim()}>
                Submit
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleSkip}>
                Skip →
              </Button>
              <Button variant="ghost" size="sm" onClick={() => loadConversation()}>
                Next Conversation
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
